/**
 * Grades read outcomes; they never trigger them (v1 plan section 3). Every
 * window uses one of two helpers, and every grade goes out through emitGrade,
 * which records it on the state for popups and starts hit-stop on PERFECT.
 */

import type { EventBuffer } from "./events";
import type { SimState } from "./state";
import type { CompiledTuning } from "./tuning";
import { Ev, Grade, type GradeId, GradeKind } from "./types";

/**
 * Grade a signed offset from a window's center (negative is early): PERFECT
 * within `perfectHalf` either side, GOOD from `goodEarly` before to `goodLate`
 * after, OK outside.
 */
export function gradeOffset(
  offset: number,
  perfectHalf: number,
  goodEarly: number,
  goodLate: number,
): GradeId {
  if (offset >= -perfectHalf && offset <= perfectHalf) return Grade.Perfect;
  if (offset >= -goodEarly && offset <= goodLate) return Grade.Good;
  return Grade.Ok;
}

/** Grade a value against nested bands: PERFECT inside [pMin, pMax], GOOD inside [gMin, gMax], OK outside. */
export function gradeBand(value: number, pMin: number, pMax: number, gMin: number, gMax: number): GradeId {
  if (value >= pMin && value <= pMax) return Grade.Perfect;
  if (value >= gMin && value <= gMax) return Grade.Good;
  return Grade.Ok;
}

/** Freeze the world for hitStopTicks (hit-stop), unless it is off or already running longer. */
export function startFreeze(s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  if (t.hitStopTicks <= 0 || s.freezeTicks >= t.hitStopTicks) return;
  s.freezeTicks = t.hitStopTicks;
  ev.push(Ev.Frozen, s.tick, s.freezeTicks);
}

/** Emit a Graded event, record it on the state, and start hit-stop on PERFECT. */
export function emitGrade(
  s: SimState,
  t: CompiledTuning,
  ev: EventBuffer,
  kind: number,
  grade: number,
  value: number,
): void {
  s.lastGradeTick = s.tick;
  s.lastGradeKind = kind;
  s.lastGrade = grade;
  ev.push(Ev.Graded, s.tick, kind, grade, value);
  // A precision obstacle should save time: stopping the world on every PERFECT used to make
  // jumping past the same obstacle faster. Keep its popup and speed lines without stopping play.
  if (grade === Grade.Perfect && !(t.hurdleFlow && (kind === GradeKind.Vault || kind === GradeKind.Slide)))
    startFreeze(s, t, ev);
}
